/**
 * SPEC-08 §4.3 — `<TieOutBadge/>` (also exported as `StatusPill`).
 *
 * Replaces the ad-hoc ✓ / ⚠ glyphs and emoji that have been used in audit
 * footers and totals (e.g. the Yevmiye "YILLIK TOPLAM" 0,03 USD Dr/Cr gap
 * that today renders with a bare ⚠ — alarming for an auditor). Variants:
 *
 *   • `pass`        — success token + check icon (true 0,00 diff)
 *   • `immaterial`  — neutral / muted (≤ materiality threshold, default 0,05)
 *   • `fail`        — destructive (genuine break above threshold)
 *
 * Helper `tieOutVariant(diff, threshold)` picks the variant from a numeric
 * diff so callers don't sprinkle the same threshold logic everywhere. The
 * pill itself is presentation only — the number is unchanged.
 *
 * Copy for the badge comes from i18n keys `common.tieOut.pass | immaterial |
 * fail`. Custom labels can be passed via the `label` prop (e.g. an inline
 * value next to the icon, or a longer "rounding diff" hint).
 */

import { Check, Minus, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";

export type TieOutVariant = "pass" | "immaterial" | "fail";

export interface TieOutBadgeProps {
  variant: TieOutVariant;
  label?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
  /** Hide the icon (e.g. when used inline next to a number). */
  iconOnly?: boolean;
  /** Hide the text label (e.g. icon-only pill at table footer). */
  labelOnly?: boolean;
}

/**
 * Pick the variant from a numeric diff (e.g. Dr − Cr). Anything within
 * `threshold` (default 0,05 — one kuruş + epsilon — matches the spec's
 * §4.3 immaterial-rounding example) is `immaterial`; an exact 0 is `pass`.
 */
export function tieOutVariant(
  diff: number,
  threshold = 0.05,
): TieOutVariant {
  if (!Number.isFinite(diff)) return "fail";
  const abs = Math.abs(diff);
  if (abs === 0) return "pass";
  if (abs <= threshold) return "immaterial";
  return "fail";
}

const STYLES: Record<TieOutVariant, string> = {
  pass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  immaterial:
    "border-border bg-muted/60 text-muted-foreground",
  fail: "border-destructive/30 bg-destructive/10 text-destructive",
};

const ICONS: Record<TieOutVariant, React.ComponentType<{ className?: string }>> = {
  pass: Check,
  immaterial: Minus,
  fail: TriangleAlert,
};

export function TieOutBadge({
  variant,
  label,
  className,
  "data-testid": testId,
  iconOnly,
  labelOnly,
}: TieOutBadgeProps) {
  const Icon = ICONS[variant];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
        STYLES[variant],
        className,
      )}
      data-testid={testId}
      data-variant={variant}
    >
      {!labelOnly && <Icon className="h-3 w-3" aria-hidden="true" />}
      {!iconOnly && label !== undefined && (
        <span className="font-medium">{label}</span>
      )}
    </span>
  );
}

/** Alias for places that want the more general "status pill" name. */
export { TieOutBadge as StatusPill };
